import React, { useState, useEffect } from 'react';
import Highlighter from 'react-highlight-words';
import { Input, Button, Space, Table, Tag, Popconfirm, Tooltip, Modal, message, Form, Select } from 'antd';
import { DeleteOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { Actions } from '../../store/actions';
import UserStatus from '../../data/UserStatus';
import UserEditForm from './UserEditForm';
import InputRules from '../../data/InputRules';
import { fetchAllUserLevels } from '../../store/actions/userLevels';

const layout = {
	labelCol: {
		span: 4,
	},
	wrapperCol: {
		span: 8,
	},
};
const tailLayout = {
	wrapperCol: {
		offset: 4,
		span: 16,
	},
};
const UserManagement = ({ users, fetchUsers, deleteUser, updateUser, updateUserType, userLevels, fetchUserLevels }) => {
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [refresh, setRefresh] = useState(false);
	const [editStatusModal, setEditStatusModal] = useState(false);
	const [editUserStatus, setEditUserStatus] = useState();
	const [editModal, setEditModal] = useState(false);
	const [editUser, setEditUser] = useState();
	const [changeUserTypeModal, setChangeUserTypeModal] = useState(false);
	const [userType, setUserType] = useState('');

	useEffect(() => {
		fetchUsers();
		fetchAllUserLevels();
	}, [refresh]);

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: function filterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			);
		},
		filterIcon: function filter(filtered) {
			return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;
		},
		onFilter: (value, record) =>
			record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
		render: function render(text) {
			return searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			);
		},
	});

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const onDelete = async (id) => {
		await deleteUser(id);
		setRefresh(!refresh);
	};

	const onFinish = async (values) => {
		await updateUser(editUserStatus._id, { status: values.status });
		setTimeout(() => {
			fetchUsers();
		}, 1000);
		setEditStatusModal(false);
	};

	const onFinishFailed = (errorInfo) => {
		message.error('Something went wrong!');
	};

	const onFinishType = async (values) => {
		await updateUserType(editUserStatus._id, {
			type: values.type,
			dealershipName: values.dealershipName,
			fullName: values.fullName,
		});
		setTimeout(() => {
			fetchUsers();
		}, 1000);
		setChangeUserTypeModal(false);
		setUserType('');
	};

	const columns = [
		{
			title: 'Full Name',
			dataIndex: 'fullName',
			key: 'fullName',
			sorter: (a, b) => a.fullName.length - b.fullName.length,
			...getColumnSearchProps('fullName'),
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			...getColumnSearchProps('email'),
		},
		{
			title: 'Phone',
			dataIndex: 'phone',
			key: 'phone',
			...getColumnSearchProps('phone'),
		},
		{
			title: 'Username',
			dataIndex: 'username',
			key: 'username',
			sorter: (a, b) => a.username?.length - b.username?.length,
			...getColumnSearchProps('username'),
		},
		{
			title: 'User Level',
			dataIndex: ['userLevel', 'name'],
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			sorter: (a, b) => a.type?.length - b.type?.length,
			render: function type(text, record) {
				return (
					<>
						{record.type === 'seller' ? <Tag color="warning">Seller</Tag> : <Tag color="green">Dealer</Tag>}
						<Tooltip title="Update Types">
							<EditOutlined
								onClick={() => {
									setEditUserStatus(record);
									setChangeUserTypeModal(!changeUserTypeModal);
								}}
							/>
						</Tooltip>
					</>
				);
			},
		},
		{
			title: 'Status Action',
			key: 'status',
			render: function status(text, record) {
				return (
					<>
						{record?.status === UserStatus.ACTIVE && <Tag color="green">Approved</Tag>}
						{record?.status === UserStatus.PENDING_VERIFICATION && <Tag color="yellow">Pending Verification</Tag>}
						{record?.status === UserStatus.SUSPENDED && <Tag color="warning">Suspended</Tag>}
						{record?.status === UserStatus.DELETED && <Tag color="red">DELETED</Tag>}
						<Tooltip title="Update Status">
							<EditOutlined
								onClick={() => {
									setEditUserStatus(record);
									setEditStatusModal(!editStatusModal);
								}}
							/>
						</Tooltip>
					</>
				);
			},
		},
		{
			title: 'Action',
			key: 'action',
			render: function action(text, record) {
				return (
					<>
						<Tooltip title="Update User">
							<EditOutlined
								onClick={() => {
									setEditUser(record);
									setEditModal(!editModal);
								}}
							/>
						</Tooltip>
						<Popconfirm
							title="Are you sure to delete this user?"
							onConfirm={() => onDelete(record._id)}
							okText="Yes"
							cancelText="No"
						>
							<DeleteOutlined />
						</Popconfirm>
					</>
				);
			},
		},
	];

	return (
		<div>
			<Table dataSource={users} columns={columns} rowKey={(record) => record._id} size="small" scroll={{ x: true }} />
			<Modal
				title={`Update ${editUserStatus?.fullName} Status`}
				centered={true}
				visible={changeUserTypeModal}
				footer={null}
				onCancel={() => {
					setChangeUserTypeModal(false);
					setUserType('');
				}}
				destroyOnClose={true}
			>
				<Form
					name="type-update-form"
					initialValues={{ remember: true }}
					onFinish={onFinishType}
					onFinishFailed={onFinishFailed}
					layout="vertical"
				>
					<Form.Item label="Type" name="type">
						<Select placeholder="Select Type" onSelect={(value) => setUserType(value)}>
							<Select.Option value="dealer">Dealer</Select.Option>
							<Select.Option value="seller">Seller</Select.Option>
						</Select>
					</Form.Item>
					{userType === 'dealer' && (
						<>
							<Form.Item
								initialValue={editUserStatus?.dealershipName}
								label="Dealership Name"
								name="dealershipName"
								rules={InputRules.required('Please enter dealership name!')}
							>
								<Input placeholder="Enter dealership name" />
							</Form.Item>
							<Form.Item
								initialValue={editUserStatus?.fullName}
								label="Full Name"
								name="fullName"
								rules={InputRules.required('Please enter name!')}
							>
								<Input placeholder="Enter full name" />
							</Form.Item>
							<Form.Item label="Level" name={['user', 'userLevel', '_id']}>
								<Select showSearch allowClear={true} placeholder="Select User Level" mode="level">
									{userLevels
										?.filter((data) => data.levelCategory === userType)
										.map((data, index) => (
											<Select.Option key={index} value={data._id}>
												{data.name}
											</Select.Option>
										))}
								</Select>
							</Form.Item>
						</>
					)}
					<Form.Item {...tailLayout}>
						<Button type="primary" htmlType="submit">
							Update User Type
						</Button>
					</Form.Item>
				</Form>
			</Modal>
			<Modal
				title={`Update ${editUserStatus?.fullName} Status`}
				centered={true}
				visible={editStatusModal}
				footer={null}
				onCancel={() => setEditStatusModal(false)}
				destroyOnClose={true}
			>
				<Form
					{...layout}
					name="status-update-form"
					initialValues={{ remember: true }}
					onFinish={onFinish}
					onFinishFailed={onFinishFailed}
				>
					<Form.Item label="Status" name="status">
						<Select placeholder="Select status">
							<Select.Option value={1}>Active</Select.Option>
							<Select.Option value={2}>Pending Verification</Select.Option>
							<Select.Option value={0}>Suspended</Select.Option>
							<Select.Option value={-1}>Deleted</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item {...tailLayout}>
						<Button type="primary" htmlType="submit">
							Update
						</Button>
					</Form.Item>
				</Form>
			</Modal>
			<Modal
				title={`Update ${editUser?.fullName}`}
				centered={true}
				visible={editModal}
				footer={null}
				onCancel={() => setEditModal(false)}
				destroyOnClose={true}
			>
				<UserEditForm closeModal={() => setEditModal(false)} user={editUser} />
			</Modal>
		</div>
	);
};

const mapStateToProps = (store) => ({
	users: store.user.users,
	userLevels: store.userLevels.userLevels,
});

const mapDispatchToProps = (dispatch) => ({
	fetchUsers: () => dispatch(Actions.fetchUsers()),
	fetchUserLevels: () => dispatch(Actions.fetchAllUserLevels()),
	deleteUser: (id) => dispatch(Actions.deleteUser(id)),
	updateUser: (id, body) => dispatch(Actions.updateUser(id, body)),
	updateUserType: (id, body) => dispatch(Actions.updateUserType(id, body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserManagement);
